import { TooltipDirective } from 'src/app/directives/tooltip.directive';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { faCheck, faWarning } from '@fortawesome/free-solid-svg-icons';
import { faSquare, faSquareCheck } from '@fortawesome/free-regular-svg-icons';
import { EnumCurrency } from '@simplyfly/shared';
import { EnumEnquiryCotationStatus } from '@simplyfly/shared';
import { getEnumEnquiryCotationStatusLabel } from 'src/app/enums/enquiry-cotation-status.helpers';
import { EnumEnquiryType } from '@simplyfly/shared';
import { EnumAcmiType, IEnquiry } from '@simplyfly/shared';
import { IAirport } from '@simplyfly/shared';
import { getAirportLocalTimeFullFromUTC, getAirportUTCFullTimeString } from 'src/app/interfaces/airport.helpers';
import { IEnquiryCotationLeg } from '@simplyfly/shared';
import { IEnquiryTrip } from '@simplyfly/shared';
import { IQuotation } from '@simplyfly/shared';
import { IQuotationTrip } from '@simplyfly/shared';
import { NgTemplateOutlet, DatePipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';

export interface IConfirmedCotationsPayload {
  selectedConfirmedCotationsId: string[];
  cotationLegsObj: { [cotationId: string]: IEnquiryCotationLeg[] };
}

@Component({
    selector: 'app-enquiry-confirmed-modal',
    templateUrl: './enquiry-confirmed-modal.component.html',
    styleUrls: ['./enquiry-confirmed-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
      TooltipDirective,
      NgTemplateOutlet,
      FaIconComponent,
      DatePipe,
      TranslateModule
    ]
})
export class EnquiryConfirmedModalComponent implements OnChanges {
  @ViewChild('modalConfirmed', { static: false }) modalConfirmedElement: ElementRef;

  @Input() enquiry: IEnquiry;
  @Input() lines: any[] = [];
  @Input() quotations: IQuotation[] = [];
  @Input() fields: { title: string; code: string }[] = [];
  @Input() legsList: any = {};
  @Input() airportsObj: { [key: string]: IAirport | null } = {};
  @Input() defaultCurrency: EnumCurrency = EnumCurrency.EUR;
  @Input() isLogged = false;
  @Input() initialSelectedCotationIds: string[] = [];

  @Output() saveConfirmed = new EventEmitter<IConfirmedCotationsPayload>();

  getEnumEnquiryCotationStatusLabel = getEnumEnquiryCotationStatusLabel;

  EnumEnquiryType = EnumEnquiryType;
  EnumEnquiryCotationStatus = EnumEnquiryCotationStatus;
  EnumAcmiType = EnumAcmiType;

  orderBy: {
    field: string;
    direction: 'asc' | 'desc';
  } = {
    field: 'status',
    direction: 'asc'
  };

  selectedConfirmedCotationsId: string[] = [];
  selectedCotationLegsObj: { [cotationId: string]: IEnquiryCotationLeg[] } = {};

  statusLabelObj: {
    [key: string]: {
      position: number;
    };
  } = {
    'waiting-for-answer': {
      position: 3
    },
    answered: {
      position: 1
    },
    rejected: {
      position: 4
    },
    'to-contact': {
      position: 2
    },
    confirmed: {
      position: 0
    },
    cancelled: {
      position: 4
    }
  };

  faWarning = faWarning;
  faCheck = faCheck;
  faSquare = faSquare;
  faSquareCheck = faSquareCheck;
  accordionId = 'confirmedCotationsAccordion';

  get validLines(): any[] {
    return (this.lines || []).filter(
      (line: any) => line.status === EnumEnquiryCotationStatus.answered
    );
  }

  get unvalidLines(): any[] {
    return (this.lines || []).filter(
      (line: any) => line.status !== EnumEnquiryCotationStatus.answered
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialSelectedCotationIds) {
      this.selectedConfirmedCotationsId = [...(this.initialSelectedCotationIds || [])];
      this.syncSelectedCotationLegs();
    }

    if (changes.lines && this.lines?.length) {
      this.sortingLines();
    }
  }

  showModal(): void {
    this.selectedConfirmedCotationsId = [...(this.initialSelectedCotationIds || [])];
    this.syncSelectedCotationLegs();
    window.$(this.modalConfirmedElement.nativeElement).modal('show');
  }

  hideModal(): void {
    if (this.modalConfirmedElement) {
      window.$(this.modalConfirmedElement.nativeElement).modal('hide');
    }
  }

  toggleConfirmedCotation(line: any): void {
    const cotationId: string = line?.cotationId;

    if (!cotationId) {
      return;
    }

    const index: number = this.selectedConfirmedCotationsId.indexOf(cotationId);

    if (index === -1) {
      this.selectedConfirmedCotationsId.push(cotationId);
      this.selectedCotationLegsObj[cotationId] = this.getLineLegs(line);
    } else {
      this.selectedConfirmedCotationsId.splice(index, 1);
      delete this.selectedCotationLegsObj[cotationId];
    }
  }

  save(): void {
    this.hideModal();
    this.saveConfirmed.emit({
      selectedConfirmedCotationsId: [...this.selectedConfirmedCotationsId],
      cotationLegsObj: Object.assign({}, this.selectedCotationLegsObj)
    });
  }

  getLineLegs(line: any): IEnquiryCotationLeg[] {
    const itinerary = this.enquiry?.itineraries?.find(
      currentItinerary => currentItinerary.id === line?.itineraryId
    );
    const latestQuotation: IQuotation | null = this.getLatestQuotation();

    if (!itinerary?.trips?.length || !line?.legsId?.length) {
      return [];
    }

    return itinerary.trips
      .filter((trip: IEnquiryTrip) => line.legsId.includes(trip.id))
      .map((trip: IEnquiryTrip, legIndex: number) => {
        const quotationTrip: IQuotationTrip | undefined =
          latestQuotation?.trips?.find(quotationTripItem => quotationTripItem.id === trip.id) ??
          latestQuotation?.trips?.find(
            quotationTripItem =>
              quotationTripItem.airportDepart === trip.airportDepart &&
              quotationTripItem.airportDestination === trip.airportDestination &&
              quotationTripItem.date === trip.date
          ) ??
          latestQuotation?.trips?.[legIndex];

        const departureDate: string = quotationTrip?.date || trip.date || '';
        const departureTime: string = quotationTrip?.time || trip.time || '';

        const mappedLeg: IEnquiryCotationLeg = {
          airportDepartId: trip.airportDepart || '',
          airportDepartTitle:
            this.airportsObj[trip.airportDepart]?.title || trip.airportDepart || '',
          airportDepartIataCode:
            this.airportsObj[trip.airportDepart]?.iataCode || trip.airportDepart || '',
          departTimezone: trip.airportDepartTimezoneId || '',
          departDateTimeLocal:
            departureDate && departureTime ? `${departureDate}T${departureTime}` : '',
          departDateLocal: departureDate,
          departTimeLocal: departureTime,
          departDateTimeUtc: '',
          departDateUtc: '',
          departTimeUtc: '',
          airportArrivalId: trip.airportDestination || '',
          airportArrivalTitle:
            this.airportsObj[trip.airportDestination]?.title || trip.airportDestination || '',
          airportArrivalIataCode:
            this.airportsObj[trip.airportDestination]?.iataCode || trip.airportDestination || '',
          arrivalTimezone: trip.airportDestinationTimezoneId || '',
          arrivalDateTimeLocal: '',
          arrivalDateLocal: '',
          arrivalTimeLocal: '',
          arrivalDateTimeUtc: '',
          arrivalDateUtc: '',
          arrivalTimeUtc: '',
          flyTimeInMin: null
        };

        this.syncLegDateTimeFromLocal(mappedLeg, 'depart');
        this.prefillArrivalFromLatestQuotation(mappedLeg, trip, legIndex, quotationTrip);

        return mappedLeg;
      });
  }

  getLegRoute(leg: IEnquiryCotationLeg): string {
    const depart: string = this.getAirportRouteLabel(leg.airportDepartId);
    const arrival: string = this.getAirportRouteLabel(leg.airportArrivalId);

    return `${depart} → ${arrival}`;
  }

  private getAirportRouteLabel(airportId: string): string {
    const airport: IAirport | null = this.airportsObj?.[airportId] || null;

    if (!airport) {
      return airportId || '';
    }

    const title: string = airport.title || airportId;
    const iataCode: string = airport.iataCode || '';
    const icaoCode: string = airport.icaoCode || '';
    const codes: string[] = [];

    if (iataCode) {
      codes.push(iataCode);
    }
    if (icaoCode) {
      codes.push(icaoCode);
    }

    return codes.length ? `${title} (${codes.join(' / ')})` : title;
  }

  onLegDateTimeInput(
    leg: IEnquiryCotationLeg,
    prefix: 'depart' | 'arrival',
    timeField: 'local' | 'utc',
    value: string
  ): void {
    const dateTimeField: string =
      prefix + (timeField === 'local' ? 'DateTimeLocal' : 'DateTimeUtc');
    const dateField: string = prefix + (timeField === 'local' ? 'DateLocal' : 'DateUtc');
    const timeFieldName: string = prefix + (timeField === 'local' ? 'TimeLocal' : 'TimeUtc');

    (leg as any)[dateTimeField] = value || '';

    const [datePart, timePart] = (value || '').split('T');
    (leg as any)[dateField] = datePart || '';
    (leg as any)[timeFieldName] = timePart || '';

    if (timeField === 'local') {
      this.syncLegDateTimeFromLocal(leg, prefix);
    } else {
      this.syncLegDateTimeFromUtc(leg, prefix);
    }
  }

  private prefillArrivalFromLatestQuotation(
    leg: IEnquiryCotationLeg,
    enquiryTrip: IEnquiryTrip,
    legIndex: number,
    quotationTripFromMap?: IQuotationTrip
  ): void {
    const quotationTrip: IQuotationTrip | undefined =
      quotationTripFromMap ??
      this.getLatestQuotation()?.trips?.find(trip => trip.id === enquiryTrip.id) ??
      this.getLatestQuotation()?.trips?.find(
        trip =>
          trip.airportDepart === enquiryTrip.airportDepart &&
          trip.airportDestination === enquiryTrip.airportDestination &&
          trip.date === enquiryTrip.date
      ) ??
      this.getLatestQuotation()?.trips?.[legIndex];

    if (!quotationTrip || !quotationTrip.date || !quotationTrip.arrivalTime) {
      return;
    }

    leg.arrivalTimezone = quotationTrip.airportDestinationTimezoneId || leg.arrivalTimezone || '';

    let arrivalDateLocal: string = leg.departDateLocal || quotationTrip.date;
    const departureTimeLocalRaw: string = quotationTrip.time || leg.departTimeLocal || '';
    const arrivalTimeLocalRaw: string = quotationTrip.arrivalTime || '';
    const departureTimeLocal: string = departureTimeLocalRaw.slice(0, 5);
    const arrivalTimeLocalFromQuotation: string = arrivalTimeLocalRaw.slice(0, 5);

    const shouldShiftToNextDay: boolean = !!(
      departureTimeLocal &&
      arrivalTimeLocalFromQuotation &&
      arrivalTimeLocalFromQuotation < departureTimeLocal
    );

    if (shouldShiftToNextDay) {
      const [year, month, day] = (arrivalDateLocal || '').split('-').map((value) => parseInt(value, 10));
      if (year && month && day) {
        const nextDateUtc: Date = new Date(Date.UTC(year, month - 1, day));
        nextDateUtc.setUTCDate(nextDateUtc.getUTCDate() + 1);
        const yearUtc: number = nextDateUtc.getUTCFullYear();
        const monthUtc: string = String(nextDateUtc.getUTCMonth() + 1).padStart(2, '0');
        const dayUtc: string = String(nextDateUtc.getUTCDate()).padStart(2, '0');
        arrivalDateLocal = `${yearUtc}-${monthUtc}-${dayUtc}`;
      }
    }

    leg.arrivalDateTimeLocal = `${arrivalDateLocal}T${quotationTrip.arrivalTime}`;
    leg.arrivalDateLocal = arrivalDateLocal;
    leg.arrivalTimeLocal = quotationTrip.arrivalTime;
    leg.arrivalDateTimeUtc = `${arrivalDateLocal}T${quotationTrip.arrivalTime}`;
    leg.arrivalDateUtc = arrivalDateLocal;
    leg.arrivalTimeUtc = quotationTrip.arrivalTime;
    this.syncLegDateTimeFromLocal(leg, 'arrival');

  }

  private getLatestQuotation(): IQuotation | null {
    if (!this.quotations?.length) {
      return null;
    }

    return [...this.quotations].sort((quotationA: IQuotation, quotationB: IQuotation) => {
      if ((quotationB.versionNumber || 0) !== (quotationA.versionNumber || 0)) {
        return (quotationB.versionNumber || 0) - (quotationA.versionNumber || 0);
      }

      const modifiedA: number = quotationA.modified ? new Date(quotationA.modified).getTime() : 0;
      const modifiedB: number = quotationB.modified ? new Date(quotationB.modified).getTime() : 0;

      return modifiedB - modifiedA;
    })[0];
  }

  getLegDateValidationMessage(leg: IEnquiryCotationLeg): string | null {
    if (!leg.departDateTimeUtc || !leg.arrivalDateTimeUtc) {
      return 'Departure and arrival dates are required.';
    }

    const departureDate: Date = new Date(leg.departDateTimeUtc);
    const arrivalDate: Date = new Date(leg.arrivalDateTimeUtc);

    if (isNaN(departureDate.getTime()) || isNaN(arrivalDate.getTime())) {
      return 'Departure and arrival dates are invalid.';
    }

    if (departureDate.getTime() === arrivalDate.getTime()) {
      return 'Departure and arrival dates cannot be the same.';
    }

    if (departureDate.getTime() > arrivalDate.getTime()) {
      return 'Departure date cannot be after arrival date.';
    }

    if (arrivalDate.getTime() < departureDate.getTime()) {
      return 'Arrival date cannot be before departure date.';
    }

    return null;
  }

  hasInvalidSelectedLegs(): boolean {
    for (const cotationId of this.selectedConfirmedCotationsId) {
      const legs: IEnquiryCotationLeg[] = this.selectedCotationLegsObj[cotationId] || [];

      if (!legs.length) {
        return true;
      }

      for (const leg of legs) {
        if (this.getLegDateValidationMessage(leg)) {
          return true;
        }
      }
    }

    return false;
  }

  private syncLegDateTimeFromLocal(leg: IEnquiryCotationLeg, prefix: 'depart' | 'arrival'): void {
    const airportId: string = prefix === 'depart' ? leg.airportDepartId : leg.airportArrivalId;
    const airport: IAirport | null = this.airportsObj?.[airportId] || null;
    const localDate: string = prefix === 'depart' ? leg.departDateLocal : leg.arrivalDateLocal;
    const localTime: string = prefix === 'depart' ? leg.departTimeLocal : leg.arrivalTimeLocal;
    const utcDateTime: string | null = airport
      ? getAirportUTCFullTimeString(airport, localDate, localTime)
      : null;

    if (utcDateTime) {
      const normalizedUtcDateTime: string = utcDateTime.replace(' ', 'T');
      const [datePart, timePart] = normalizedUtcDateTime.split('T');

      if (prefix === 'depart') {
        leg.departDateTimeUtc = normalizedUtcDateTime;
        leg.departDateUtc = datePart || '';
        leg.departTimeUtc = timePart || '';
      } else {
        leg.arrivalDateTimeUtc = normalizedUtcDateTime;
        leg.arrivalDateUtc = datePart || '';
        leg.arrivalTimeUtc = timePart || '';
      }
    }
  }

  private syncLegDateTimeFromUtc(leg: IEnquiryCotationLeg, prefix: 'depart' | 'arrival'): void {
    const airportId: string = prefix === 'depart' ? leg.airportDepartId : leg.airportArrivalId;
    const airport: IAirport | null = this.airportsObj?.[airportId] || null;
    const utcDate: string = prefix === 'depart' ? leg.departDateUtc : leg.arrivalDateUtc;
    const utcTime: string = prefix === 'depart' ? leg.departTimeUtc : leg.arrivalTimeUtc;
    const localDateTime: string | null = airport
      ? getAirportLocalTimeFullFromUTC(airport, utcDate, utcTime)
      : null;

    if (localDateTime) {
      const normalizedLocalDateTime: string = localDateTime.replace(' ', 'T');
      const [datePart, timePart] = normalizedLocalDateTime.split('T');

      if (prefix === 'depart') {
        leg.departDateTimeLocal = normalizedLocalDateTime;
        leg.departDateLocal = datePart || '';
        leg.departTimeLocal = timePart || '';
      } else {
        leg.arrivalDateTimeLocal = normalizedLocalDateTime;
        leg.arrivalDateLocal = datePart || '';
        leg.arrivalTimeLocal = timePart || '';
      }
    }
  }

  private syncSelectedCotationLegs(): void {
    this.selectedCotationLegsObj = {};

    for (const line of this.lines || []) {
      if (line?.cotationId && this.selectedConfirmedCotationsId.includes(line.cotationId)) {
        this.selectedCotationLegsObj[line.cotationId] = this.getLineLegs(line);
      }
    }
  }

  formatPrice(value: number, currency: string = 'EUR'): string {
    const formatter = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2
    });

    return formatter.format(value);
  }

  sortField(field: string): void {
    if (this.orderBy.field === field) {
      this.orderBy.direction = this.orderBy.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.orderBy.field = field;
      this.orderBy.direction = 'asc';
    }

    this.sortingLines();
  }

  sortingLines(): void {
    this.lines.sort((a, b) => {
      switch (this.orderBy.field) {
        case 'status':
          if (a.status && b.status) {
            if (this.statusLabelObj[a.status].position < this.statusLabelObj[b.status].position) {
              return this.orderBy.direction === 'asc' ? -1 : 1;
            }
            if (this.statusLabelObj[a.status].position > this.statusLabelObj[b.status].position) {
              return this.orderBy.direction === 'asc' ? 1 : -1;
            }
          }
          break;
        default:
          if (a[this.orderBy.field] < b[this.orderBy.field]) {
            return this.orderBy.direction === 'asc' ? -1 : 1;
          }
          if (a[this.orderBy.field] > b[this.orderBy.field]) {
            return this.orderBy.direction === 'asc' ? 1 : -1;
          }
          if (a[this.orderBy.field] === b[this.orderBy.field]) {
            return this.statusLabelObj[a.status].position < this.statusLabelObj[b.status].position
              ? -1
              : 1;
          }
          break;
      }
      return 0;
    });

    this.updateFloatingScroll();

    setTimeout(() => {
      this.updateFloatingScroll();
    }, 1000);

    setTimeout(() => {
      this.updateFloatingScroll();
    }, 3000);

    setTimeout(() => {
      this.updateFloatingScroll();
    }, 5000);
  }

  updateFloatingScroll(): void {
    window.$('.table-responsive').floatingScroll('init', {
      orientation: 'horizontal'
    });
  }
}
